/**
 * Canonical event_key domain grouping for admin debug views.
 *
 * Naming standard: <domain>_<action>_<result>
 * Examples: activity_save_failed, booking_cancel_failed, producer_publish_failed
 *
 * This mapping is used in the Host Debug View to group system_events
 * into operational domains. It does NOT rename existing event_keys —
 * it maps them to display groups via prefix matching.
 */

export type EventDomain =
  | 'activities'
  | 'publishing'
  | 'bookings'
  | 'media'
  | 'orders'
  | 'contact'
  | 'admin'
  | 'other';

export interface DomainMeta {
  label: string;
  icon: string; // lucide icon name for reference
}

export const DOMAIN_META: Record<EventDomain, DomainMeta> = {
  activities: { label: 'Aktiviteter', icon: 'Calendar' },
  publishing: { label: 'Publicering', icon: 'Globe' },
  bookings: { label: 'Bokningar', icon: 'Ticket' },
  media: { label: 'Media', icon: 'Image' },
  orders: { label: 'Beställningar', icon: 'ShoppingBag' },
  contact: { label: 'Kontakt', icon: 'Mail' },
  admin: { label: 'Admin', icon: 'Shield' },
  other: { label: 'Övrigt', icon: 'MoreHorizontal' },
};

/**
 * Prefix-based rules evaluated in order. First match wins.
 */
const PREFIX_RULES: [string, EventDomain][] = [
  ['activity_', 'activities'],
  ['event_', 'activities'],
  ['producer_publish', 'publishing'],
  ['producer_unpublish', 'publishing'],
  ['place_publish', 'publishing'],
  ['place_unpublish', 'publishing'],
  ['booking_', 'bookings'],
  ['host_cancel_booking', 'bookings'],
  ['media_', 'media'],
  ['upload_', 'media'],
  ['order_', 'orders'],
  ['pickup_order', 'orders'],
  ['checkout_', 'orders'],
  ['producer_contact', 'contact'],
  ['admin.', 'admin'],
  ['editor_', 'admin'],
];

/**
 * Classify an event_key into a domain for grouping.
 * Uses prefix matching — fast, no regex.
 */
export function classifyEventKey(eventKey: string): EventDomain {
  const key = eventKey.toLowerCase();
  for (const [prefix, domain] of PREFIX_RULES) {
    if (key.startsWith(prefix)) return domain;
  }
  return 'other';
}

/**
 * Group an array of events by domain.
 */
export function groupByDomain<T extends { event_key: string }>(
  events: T[],
): Record<EventDomain, T[]> {
  const groups: Record<EventDomain, T[]> = {
    activities: [],
    publishing: [],
    bookings: [],
    media: [],
    orders: [],
    contact: [],
    admin: [],
    other: [],
  };
  for (const ev of events) {
    groups[classifyEventKey(ev.event_key)].push(ev);
  }
  return groups;
}
